// General utilities for the application

// Function to copy to clipboard
export const copyToClipboard = async (text, type = 'Text') => {
  try {
    await navigator.clipboard.writeText(text);
    return {
      success: true,
      message: `${type} copied to clipboard`
    };
  } catch (error) {
    return {
      success: false,
      message: `Failed to copy ${type.toLowerCase()}`
    };
  }
};

// Function to truncate text
export const truncateText = (text, maxLength = 50) => {
  if (!text) return '';
  return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
};

/**
 * Format phone for display. Supports:
 * - Local: XXX-XXX-XXXX (e.g. 904-415-2299), 10 digits
 * - International: +18507862330 (+1 + 10 digits)
 * @param {string} value - Raw phone (digits or +digits)
 * @returns {string} "XXX-XXX-XXXX" or "+1XXXXXXXXXX"
 */
export const formatPhoneDisplay = (value) => {
  if (!value) return '';
  const digits = String(value).replace(/\D/g, '');
  if (digits.length === 11 && digits[0] === '1') {
    return `+${digits}`;
  }
  const d = digits.slice(0, 10);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)}-${d.slice(3)}`;
  return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
};

import {
  parseDateSafe,
  parseDateTimeSafe,
  parseUtcDateTime,
  isDateOnlyApiValue,
  hasDateTimeComponent
} from './dateHelper';
import { DISPLAY_DATE_SHORT_US_OPTIONS } from '../constants/dateFormats.js';
import { getUserTimezone } from './userTimezone';

// Optional company-wide override (all users see this zone when set)
const APP_TIMEZONE = process.env.VUE_APP_TIMEZONE || undefined;

/**
 * Resolve timezone for display: env override, then user profile, then UTC.
 * @param {string|undefined} override
 * @returns {string}
 */
export function resolveDisplayTimezone(override) {
  if (override) return override;
  if (APP_TIMEZONE) return APP_TIMEZONE;
  return getUserTimezone();
}

/**
 * Parse API value for display: calendar dates vs UTC instants.
 * @param {string|Date|null} dateString
 * @returns {Date|null}
 */
export function parseApiDateForDisplay(dateString) {
  if (dateString === null || dateString === undefined || dateString === '') return null;
  if (dateString instanceof Date) {
    return Number.isNaN(dateString.getTime()) ? null : dateString;
  }
  if (isDateOnlyApiValue(dateString)) {
    return parseDateSafe(dateString);
  }
  if (hasDateTimeComponent(dateString)) {
    return parseUtcDateTime(dateString) || parseDateTimeSafe(dateString);
  }
  return parseDateSafe(dateString) || parseUtcDateTime(dateString);
}

/**
 * Format date for display. Calendar-only fields stay calendar-only; datetimes use user timezone.
 * @param {string|Date|null} dateString - YYYY-MM-DD, MySQL datetime (UTC), or Date
 * @param {Object} options - Intl.DateTimeFormat options; optional timeZone override
 * @returns {string}
 */
export const formatDate = (dateString, options = {}) => {
  if (dateString === null || dateString === undefined) return '';

  const tz = resolveDisplayTimezone(options.timeZone);
  const { timeZone: _ignored, ...intlOptions } = options;

  const defaultOptions = {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  };

  const date = parseApiDateForDisplay(dateString);
  if (!date || Number.isNaN(date.getTime())) {
    return typeof dateString === 'string' ? dateString : '';
  }

  const finalOptions = { ...defaultOptions, ...intlOptions };
  if (hasDateTimeComponent(dateString)) {
    finalOptions.timeZone = tz;
  }
  return date.toLocaleDateString('en-US', finalOptions);
};

/**
 * Format date with time for display in the user's timezone (API datetimes are UTC).
 * @param {string|Date|null} dateString - YYYY-MM-DD HH:mm:ss (UTC), ISO, or Date
 * @param {Object} options - Intl.DateTimeFormat options
 * @returns {string}
 */
export const formatDateTime = (dateString, options = {}) => {
  if (dateString === null || dateString === undefined) return '-';

  const tz = resolveDisplayTimezone(options.timeZone);
  const { timeZone: _ignored, ...intlOptions } = options;

  const defaultOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    timeZone: tz,
  };

  const date = parseApiDateForDisplay(dateString);
  if (!date || Number.isNaN(date.getTime())) {
    return typeof dateString === 'string' ? dateString : '-';
  }

  const finalOptions = { ...defaultOptions, ...intlOptions };
  return date.toLocaleString('en-US', finalOptions);
};

/**
 * Compact textual datetime: "Jul 30, 2026, 14:35" (en-US date + 24h time, no seconds).
 * Use for sentences/timelines; formatDateTime() remains numeric with seconds for audit/detail.
 * @param {string|Date|null} dateString
 * @param {Object} [options]
 * @returns {string}
 */
export const formatDateTimeShort = (dateString, options = {}) => {
  if (dateString === null || dateString === undefined) return '-';

  const tz = resolveDisplayTimezone(options.timeZone);
  const { timeZone: _ignored, ...intlOptions } = options;
  const date = parseApiDateForDisplay(dateString);
  if (!date || Number.isNaN(date.getTime())) {
    return typeof dateString === 'string' ? dateString : '-';
  }

  const datePart = date.toLocaleDateString('en-US', {
    ...DISPLAY_DATE_SHORT_US_OPTIONS,
    ...intlOptions,
    timeZone: tz,
  });
  const timePart = date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: tz,
    ...intlOptions,
  });
  return `${datePart}, ${timePart}`;
};

/**
 * Compact textual date for lists (e.g. inbox activity): "Jul 30, 2026" (en-US).
 * Contract differs from formatDate() which returns numeric "07/30/2026".
 * @param {string|Date|null} dateString
 * @param {Object} [options]
 * @returns {string}
 */
export const formatDateShort = (dateString, options = {}) => {
  if (!dateString) return '—';
  const tz = resolveDisplayTimezone(options.timeZone);
  const { timeZone: _ignored, ...intlOptions } = options;
  const date = parseApiDateForDisplay(dateString);
  if (!date || Number.isNaN(date.getTime())) return '—';
  const opts = {
    ...DISPLAY_DATE_SHORT_US_OPTIONS,
    ...intlOptions,
  };
  if (hasDateTimeComponent(dateString)) {
    opts.timeZone = tz;
  }
  return date.toLocaleDateString('en-US', opts);
};

/**
 * Get the configured app timezone override (if any).
 * @returns {string|undefined}
 */
export const getAppTimezone = () => APP_TIMEZONE;

// Function to check outdated dates (older than a week).
export const isDateOlderThanWeek = (dateString) => {
  if (!dateString) return false;
  const date = parseApiDateForDisplay(dateString) || new Date(dateString);
  if (Number.isNaN(date.getTime())) return false;
  const weekAgo = new Date();
  weekAgo.setDate(weekAgo.getDate() - 7);
  return date < weekAgo;
};

// Function to get severity status
export const getStatusSeverity = (status) => {
  if (typeof status === 'boolean') {
    return status ? 'success' : 'danger';
  }
  return status === 1 ? 'success' : 'danger';
};

// Function to get status label
export const getStatusLabel = (status, activeLabel = 'Active', inactiveLabel = 'Inactive') => {
  if (typeof status === 'boolean') {
    return status ? activeLabel : inactiveLabel;
  }
  return status === 1 ? activeLabel : inactiveLabel;
};

// Debounce function
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// Function to validate email
export const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

// Function to sanitize strings
export const sanitizeString = (str) => {
  if (!str) return '';
  return str.trim().replace(/[<>]/g, '');
};

// Constants for severity
export const SEVERITY = {
  SUCCESS: 'success',
  INFO: 'info',
  WARN: 'warn',
  WARNING: 'warning',
  ERROR: 'error',
  DANGER: 'danger',
  SECONDARY: 'secondary'
};

// Constants for toast duration
export const TOAST_DURATION = {
  SHORT: 2000,
  MEDIUM: 3000,
  LONG: 5000
};

/**
 * Decode JWT token payload (without verification)
 * @param {string} token - JWT token
 * @returns {object|null} Decoded payload or null if invalid
 */
export const decodeJWT = (token) => {
  if (!token) return null;
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;

    const payload = parts[1];
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    return null;
  }
};

/**
 * Check if current user has super_admin role
 * @param {string} token - JWT token from localStorage
 * @returns {boolean} True if user has super_admin role
 */
export const isSuperAdmin = (token) => {
  if (!token) return false;
  const decoded = decodeJWT(token);
  if (!decoded || !decoded.data || !decoded.data.roles) {
    console.warn('isSuperAdmin: Invalid token structure', { decoded, hasData: !!decoded?.data, hasRoles: !!decoded?.data?.roles });
    return false;
  }

  const roles = decoded.data.roles;
  const hasSuperAdmin = Array.isArray(roles) && roles.some(role => {
    if (typeof role === 'object' && role !== null) {
      return role.code === 'super_admin';
    }
    if (typeof role === 'string') {
      return role === 'super_admin';
    }
    return false;
  });

  console.log('isSuperAdmin check:', { roles, hasSuperAdmin });
  return hasSuperAdmin;
};

/**
 * Load and format US states data for dropdown
 * @returns {Array} Array of state objects with label and value properties
 */
export const loadUSStates = () => {
  const statesData = {
    "AL": "Alabama",
    "AK": "Alaska",
    "AS": "American Samoa",
    "AZ": "Arizona",
    "AR": "Arkansas",
    "CA": "California",
    "CO": "Colorado",
    "CT": "Connecticut",
    "DE": "Delaware",
    "DC": "District Of Columbia",
    "FM": "Federated States Of Micronesia",
    "FL": "Florida",
    "GA": "Georgia",
    "GU": "Guam",
    "HI": "Hawaii",
    "ID": "Idaho",
    "IL": "Illinois",
    "IN": "Indiana",
    "IA": "Iowa",
    "KS": "Kansas",
    "KY": "Kentucky",
    "LA": "Louisiana",
    "ME": "Maine",
    "MH": "Marshall Islands",
    "MD": "Maryland",
    "MA": "Massachusetts",
    "MI": "Michigan",
    "MN": "Minnesota",
    "MS": "Mississippi",
    "MO": "Missouri",
    "MT": "Montana",
    "NE": "Nebraska",
    "NV": "Nevada",
    "NH": "New Hampshire",
    "NJ": "New Jersey",
    "NM": "New Mexico",
    "NY": "New York",
    "NC": "North Carolina",
    "ND": "North Dakota",
    "MP": "Northern Mariana Islands",
    "OH": "Ohio",
    "OK": "Oklahoma",
    "OR": "Oregon",
    "PW": "Palau",
    "PA": "Pennsylvania",
    "PR": "Puerto Rico",
    "RI": "Rhode Island",
    "SC": "South Carolina",
    "SD": "South Dakota",
    "TN": "Tennessee",
    "TX": "Texas",
    "UT": "Utah",
    "VT": "Vermont",
    "VI": "Virgin Islands",
    "VA": "Virginia",
    "WA": "Washington",
    "WV": "West Virginia",
    "WI": "Wisconsin",
    "WY": "Wyoming"
  };

  return Object.entries(statesData).map(([code, name]) => ({
    label: `${code}: ${name}`,
    value: code
  })).sort((a, b) => a.label.localeCompare(b.label));
};
